import { useEffect, useState } from "react";
import AdminShell from "@/components/admin/AdminShell";
import MaskedField from "@/components/admin/MaskedField";
import { adminApi, API_BASE } from "@/lib/adminApi";
import { readAdminToken } from "@/components/admin/AdminGuard";
import { useT } from "@/i18n/LanguageContext";

/**
 * /admin/settings — Complete configuration center.
 *
 * SECURITY: no plaintext secrets sourced from frontend bundle. Values in
 * MaskedField are either constructed client-side (the admin's own token
 * they entered at login) or derived from server flags (present/absent).
 * Everything else displays `••••••••` with a "Configured" / "Not set" tag.
 */

const SECTION_CLS = "border border-gold/15 bg-white/[0.02] p-5 md:p-6";

function Section({ title, subtitle, children, testId }) {
  return (
    <section className="mb-10" data-testid={testId}>
      <h2 className="font-serif text-xl text-bone mb-1">{title}</h2>
      {subtitle && <p className="text-sm text-bone/50 mb-4 max-w-2xl">{subtitle}</p>}
      {!subtitle && <div className="mb-4" />}
      <div className={SECTION_CLS}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
      </div>
    </section>
  );
}

function FlagRow({ label, value, hint, testId }) {
  return (
    <div className="border border-white/10 bg-white/[0.02] p-4" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] tracking-[0.28em] uppercase text-bone/60">{label}</p>
        <span
          className={`text-[10px] tracking-[0.22em] uppercase px-2 py-0.5 border ${value ? "text-emerald-400 border-emerald-500/40" : "text-bone/50 border-white/15"}`}
        >
          {value ? "On" : "Off"}
        </span>
      </div>
      {hint && <p className="text-[11px] text-bone/50">{hint}</p>}
    </div>
  );
}

export default function AdminSettings() {
  const { t } = useT();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const adminToken = readAdminToken();

  useEffect(() => {
    adminApi.summary().then(setSummary).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const seo = summary?.autoseo || {};

  // Test Connection — pings the admin summary endpoint with the stored token.
  const testAdminAuth = async () => {
    try { await adminApi.summary(); return true; } catch { return false; }
  };

  return (
    <AdminShell title={t("admin.settings.title")}>
      <p className="text-sm text-bone/60 -mt-6 mb-8 max-w-2xl">
        {t("admin.settings.intro")}
      </p>

      {/* GENERAL */}
      <Section title={t("admin.settings.general")} subtitle={t("admin.settings.general.sub")} testId="settings-general">
        <div className="border border-white/10 bg-white/[0.02] p-4">
          <p className="text-[10px] tracking-[0.28em] uppercase text-bone/60 mb-2">{t("admin.settings.publicDomain")}</p>
          <code className="font-mono text-[13px] text-bone/85">https://stoicos.ai</code>
        </div>
        <div className="border border-white/10 bg-white/[0.02] p-4">
          <p className="text-[10px] tracking-[0.28em] uppercase text-bone/60 mb-2">{t("admin.settings.apiBase")}</p>
          <code className="font-mono text-[13px] text-bone/85 truncate block">{API_BASE || "(same origin)"}</code>
        </div>
      </Section>

      {/* AUTHENTICATION */}
      <Section title={t("admin.settings.auth")} subtitle={t("admin.settings.auth.sub")} testId="settings-auth">
        <MaskedField
          testId="masked-admin-token"
          label="ADMIN_TOKEN (session)"
          value={adminToken}
          present={!!adminToken}
          canReveal
          onTest={testAdminAuth}
        />
        <MaskedField
          testId="masked-autoseo-token"
          label="AUTOSEO_WEBHOOK_TOKEN"
          value=""
          present={!!seo.enabled}
          helper="Server-side only. Rotate via env vars."
        />
      </Section>

      {/* AI PROVIDERS */}
      <Section title={t("admin.settings.ai")} subtitle={t("admin.settings.ai.sub")} testId="settings-ai">
        <MaskedField testId="masked-openai"    label="OpenAI"        value="" present={false} helper={t("admin.label.notSet")} />
        <MaskedField testId="masked-anthropic" label="Anthropic"     value="" present={false} helper={t("admin.label.notSet")} />
        <MaskedField testId="masked-gemini"    label="Google Gemini" value="" present={false} helper={t("admin.label.notSet")} />
        <MaskedField testId="masked-emergent"  label="Emergent LLM Key" value="" present={false} helper="Universal key, enable per feature" />
      </Section>

      {/* API KEYS */}
      <Section title={t("admin.settings.apis")} subtitle={t("admin.settings.apis.sub")} testId="settings-apis">
        <MaskedField testId="masked-resend"  label="Resend (email)"     value="" present />
        <MaskedField testId="masked-stripe"  label="Stripe"             value="" present={false} helper={t("admin.label.notSet")} />
        <MaskedField testId="masked-clarity" label="Microsoft Clarity"  value="" present hint="Client-side only" />
        <MaskedField testId="masked-ga4"     label="Google Analytics 4" value="" present hint="Client-side only" />
      </Section>

      {/* AUTOSEO */}
      <Section title={t("admin.settings.autoseo")} subtitle={t("admin.settings.autoseo.sub")} testId="settings-autoseo">
        <FlagRow testId="flag-autoseo-enabled" label={t("admin.autoseo.webhookStatus")} value={!!seo.enabled} hint={seo.enabled ? t("admin.autoseo.accepting") : t("admin.autoseo.tokenMissing")} />
        <FlagRow testId="flag-autoseo-lang"    label="EN/NL" value={true} hint="Detects EN/NL per payload" />
      </Section>

      {/* ENVIRONMENT */}
      <Section title={t("admin.settings.env")} subtitle={t("admin.settings.env.sub")} testId="settings-env">
        <FlagRow testId="flag-env-mongo"  label="MongoDB"  value={true} hint={t("admin.label.connected")} />
        <FlagRow testId="flag-env-cors"   label="CORS"     value={true} hint="Wildcard origins" />
        <FlagRow testId="flag-env-resend" label="Resend"   value={true} hint="Transactional email" />
        <FlagRow testId="flag-env-region" label={t("admin.system.region")} value={true} hint="EU" />
      </Section>

      {/* FEATURE FLAGS */}
      <Section title={t("admin.settings.flags")} subtitle={t("admin.settings.flags.sub")} testId="settings-flags">
        <FlagRow testId="flag-agora"       label="/agora"                 value={true}  />
        <FlagRow testId="flag-watch"       label="/watch"                 value={true}  />
        <FlagRow testId="flag-cards"       label="/stoic-training-cards"  value={true}  />
        <FlagRow testId="flag-marcus-poc"  label="D-ID Marcus POC"        value={false} hint="Requires D-ID key" />
      </Section>

      {/* JOURNAL / ANALYTICS / DOWNLOADS */}
      <Section title={t("admin.settings.journal")} subtitle={t("admin.settings.journal.sub")} testId="settings-journal">
        <FlagRow testId="flag-journal-drafts"    label="AutoSEO → Draft only" value={true} hint="Never auto-published" />
        <FlagRow testId="flag-journal-bilingual" label="EN / NL"              value={true} hint="Strict isolation" />
      </Section>
      <Section title={t("admin.settings.analyticsFlags")} subtitle={t("admin.settings.analyticsFlags.sub")} testId="settings-analytics-flags">
        <FlagRow testId="flag-ga4"     label="GA4 pageviews"      value={true}  />
        <FlagRow testId="flag-clarity" label="Clarity heatmaps"   value={true}  />
        <FlagRow testId="flag-server-analytics" label="Server-side event pipeline" value={false} hint="Roadmap" />
      </Section>
      <Section title={t("admin.settings.downloadsFlags")} subtitle={t("admin.settings.downloadsFlags.sub")} testId="settings-downloads-flags">
        <FlagRow testId="flag-appstore"  label="App Store"   value={true}  />
        <FlagRow testId="flag-playstore" label="Play Store"  value={true}  />
      </Section>

      {loading && <p className="text-sm text-bone/50">{t("admin.label.loading")}</p>}
    </AdminShell>
  );
}
